"use client";

import * as yup from "yup";
import { ChangeEvent, useState } from "react";
import { TextField, Button, Grid } from "@mui/material";
import useToast from "@/stores/toast";
import axios from "axios";
import config from "@/app.config";
import { getMessage, getYupErrorMessages } from "@/lib/error";

const schema = yup.object({
  name: yup
    .string()
    .required("Name is required")
    .max(255, "Name must not exceed 255 characters"),
  email: yup
    .string()
    .required("Email is required")
    .email("Invalid email address")
    .max(255, "Email must not exceed 255 characters")
    .transform((value) => (value ? value.toLowerCase() : "")),
  phone: yup
    .string()
    .required("Phone number is required")
    .matches(/^[0-9]+$/, "Phone number must contain only digits")
    .length(10, "Phone number must be exactly 10 digits"),
  message: yup
    .string()
    .required("Message is required")
    .max(5000, "Message must not exceed 5000 characters"),
  brand_name: yup
    .string()
    .required("Brand name is required")
    .max(255, "Brand name must not exceed 255 characters"),
  model_name: yup
    .string()
    .required("Model name is required")
    .max(255, "Model name must not exceed 255 characters"),
  timeslot: yup
    .string()
    .required("Timeslot is required")
    .max(255, "Timeslot must not exceed 255 characters"),
  drone_serial_number: yup
    .string()
    .required("Drone serial number is required")
    .max(255, "Drone serial number must not exceed 255 characters"),
});

export default function Form() {
  const INITIAL_VALUE = {
    name: "",
    phone: "",
    email: "",
    brand_name: "",
    model_name: "",
    message: "",
    timeslot: "",
    drone_serial_number: "",
  };
  const { toast } = useToast();
  const [form, setForm] = useState(INITIAL_VALUE);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  const handleChange = (
    e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleSubmit = async () => {
    try {
      setLoading(true);
      setErrors({});

      await schema.validate(form, { abortEarly: false });

      await axios.post(`${config.app.apiBaseUrl}/api/v2/drone-repair`, form, {
        headers: {
          Accept: "application/json",
        },
        withCredentials: true,
      });

      toast({
        error: false,
        message: "Thank you for contacting us. We will get back to you soon.",
      });

      setForm(INITIAL_VALUE);
    } catch (error: unknown) {
      const errors = getYupErrorMessages(error);

      if (errors) {
        setErrors(errors);
        return;
      }

      toast({
        error: true,
        message: getMessage(error),
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <section
      className="mx-auto max-w-5xl p-6 md:p-8 md:rounded-2xl"
      style={{
        background: "linear-gradient(135deg, #EEF7E9, #E4F4F2)",
      }}
    >
      <Grid container spacing={{ xs: 2, md: 4 }}>
        <Grid size={{ xs: 12, sm: 6, md: 6 }}>
          <TextField
            label="Full Name"
            name="name"
            variant="outlined"
            value={form.name}
            onChange={handleChange}
            required
            error={!!errors.name}
            helperText={errors.name}
            fullWidth
          />
        </Grid>

        <Grid size={{ xs: 12, sm: 6, md: 6 }}>
          <TextField
            label="Phone Number"
            name="phone"
            variant="outlined"
            type="tel"
            value={form.phone}
            onChange={handleChange}
            required
            error={!!errors.phone}
            helperText={errors.phone}
            fullWidth
          />
        </Grid>

        <Grid size={{ xs: 12, sm: 6, md: 6 }}>
          <TextField
            label="Email Address"
            name="email"
            variant="outlined"
            type="email"
            value={form.email}
            onChange={handleChange}
            required
            error={!!errors.email}
            helperText={errors.email}
            fullWidth
          />
        </Grid>

        <Grid size={{ xs: 12, sm: 6, md: 6 }}>
          <TextField
            label="Drone Brand"
            name="brand_name"
            variant="outlined"
            value={form.brand_name}
            onChange={handleChange}
            required
            error={!!errors.brand_name}
            helperText={errors.brand_name}
            fullWidth
          />
        </Grid>

        <Grid size={{ xs: 12, sm: 6, md: 6 }}>
          <TextField
            label="Model Name"
            name="model_name"
            variant="outlined"
            value={form.model_name}
            onChange={handleChange}
            required
            error={!!errors.model_name}
            helperText={errors.model_name}
            fullWidth
          />
        </Grid>

        <Grid size={{ xs: 12, sm: 6, md: 6 }}>
          <TextField
            label="Drone Serial Number"
            name="drone_serial_number"
            variant="outlined"
            value={form.drone_serial_number}
            onChange={handleChange}
            required
            fullWidth
            error={!!errors.drone_serial_number}
            helperText={errors.drone_serial_number}
          />
        </Grid>

        <TextField
          label="Error / Issue Faced"
          name="message"
          variant="outlined"
          multiline
          minRows={4}
          value={form.message}
          onChange={handleChange}
          required
          error={!!errors.message}
          helperText={errors.message}
          fullWidth
        />

        <TextField
          label="Best Available Time to Call Back"
          name="timeslot"
          variant="outlined"
          value={form.timeslot}
          onChange={handleChange}
          required
          fullWidth
          error={!!errors.timeslot}
          helperText={
            errors.timeslot
              ? errors.timeslot
              : "For urgent case, please contact our service team directly."
          }
        />
      </Grid>

      <Button
        type="submit"
        variant="contained"
        fullWidth
        size="large"
        sx={{ mt: 3 }}
        disabled={loading}
        loading={loading}
        onClick={handleSubmit}
      >
        Submit Repair Request
      </Button>
    </section>
  );
}
